import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldCheck, Users, Plus, X, Loader2, Check, Copy, KeyRound, Trash2, Power, Pencil, Wand2, Crown, Mail, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Équipe & accès (Superadmin uniquement).
// Création de comptes « agent » et choix précis des onglets de l'admin que chacun peut voir ET utiliser
// (le serveur refuse toute requête hors des onglets accordés, pas seulement le menu).
// ============================================================

const api = (path, opts = {}) => fetch(`${API_URL}/api/admin/staff${path}`, {
  ...opts, headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
}).then(r => r.json());

// accès types, pour aller vite (le Superadmin peut ensuite cocher / décocher à la main)
const PRESETS = [
  { label: 'Agent support', keys: ['support_admin', 'agent_offers', 'service_requests', 'whatsapp', 'bot_studio', 'official_pages', 'trial_management'] },
  { label: 'Comptable / paiements', keys: ['agent_offers', 'credit_orders', 'ia_studio_video_admin', 'voice_split_admin', 'utile_admin', 'cm_studio', 'admin_subscriptions', 'clients_credits', 'user_activity'] },
  { label: 'Lecture des chiffres', keys: ['dashboard', 'user_activity', 'ia_studio_consumption', 'llm_performance'] },
];

const randomPassword = () => {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const a = new Uint32Array(12); crypto.getRandomValues(a);
  return [...a].map(n => chars[n % chars.length]).join('');
};

function PermPicker({ tabs, value, onChange }) {
  const groups = useMemo(() => {
    const g = {};
    tabs.forEach(t => { (g[t.group] = g[t.group] || []).push(t); });
    return g;
  }, [tabs]);
  const set = new Set(value);
  const toggle = (k) => onChange(set.has(k) ? value.filter(x => x !== k) : [...value, k]);
  const toggleGroup = (items) => {
    const all = items.every(t => set.has(t.key));
    const keys = items.map(t => t.key);
    onChange(all ? value.filter(k => !keys.includes(k)) : [...new Set([...value, ...keys])]);
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-gray-400 mr-1 flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" /> Accès types :</span>
        {PRESETS.map(p => <button key={p.label} type="button" onClick={() => onChange(p.keys.filter(k => tabs.some(t => t.key === k)))} className="px-2.5 py-1 rounded-full text-xs bg-violet-500/15 border border-violet-400/30 hover:bg-violet-500/25">{p.label}</button>)}
        <button type="button" onClick={() => onChange(tabs.map(t => t.key))} className="px-2.5 py-1 rounded-full text-xs bg-white/10 border border-white/15 hover:bg-white/20">Tout cocher</button>
        <button type="button" onClick={() => onChange([])} className="px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 hover:bg-white/10">Tout décocher</button>
      </div>
      {Object.entries(groups).map(([g, items]) => (
        <div key={g} className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
          <button type="button" onClick={() => toggleGroup(items)} className="text-[11px] font-bold tracking-widest uppercase text-gray-400 hover:text-white mb-2">{g}</button>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {items.map(t => (
              <label key={t.key} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer border text-sm transition ${set.has(t.key) ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.07]'}`}>
                <input type="checkbox" checked={set.has(t.key)} onChange={() => toggle(t.key)} className="accent-emerald-400 w-4 h-4" /> {t.label}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-3 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} my-auto rounded-2xl bg-slate-900 border border-white/15 shadow-2xl p-5 space-y-4`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3"><h3 className="font-bold text-white">{title}</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400"><X className="w-4 h-4" /></button></div>
        {children}
      </div>
    </div>
  );
}

const inputCls = 'w-full bg-black/30 border border-white/15 focus:border-violet-400/70 rounded-xl px-3 py-2 text-sm text-white outline-none';

function StaffForm({ tabs, initial, onClose, onDone }) {
  const editing = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [password, setPassword] = useState('');
  const [perms, setPerms] = useState(initial?.perms?.includes('*') ? [] : (initial?.perms || []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [promote, setPromote] = useState(null);
  const [created, setCreated] = useState(null);

  const submit = async (forcePromote) => {
    setBusy(true); setErr('');
    const body = editing ? { name, perms, ...(password ? { password } : {}) } : { name, email, password, perms, promote: !!forcePromote };
    const r = editing ? await api(`/${initial.user_id}`, { method: 'PUT', body: JSON.stringify(body) }) : await api('', { method: 'POST', body: JSON.stringify(body) });
    setBusy(false);
    if (r.success) {
      if (!editing) { setCreated({ email, password: password || '(inchangé : son mot de passe actuel)' }); onDone(); } else { onDone(); onClose(); }
    } else if (r.code === 'exists') setPromote(r);
    else setErr(r.message || 'Erreur');
  };

  if (created) {
    const text = `Accès admin TeckiA\nEmail : ${created.email}\nMot de passe : ${created.password}`;
    return (
      <Modal title="Compte agent créé ✓" onClose={onClose}>
        <p className="text-sm text-gray-300">Transmettez ces identifiants à l'agent (le mot de passe ne sera plus affiché) :</p>
        <pre className="bg-black/40 border border-white/10 rounded-xl p-3 text-sm whitespace-pre-wrap">{text}</pre>
        <div className="flex gap-2">
          <button onClick={() => navigator.clipboard?.writeText(text)} className="flex-1 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-semibold flex items-center justify-center gap-2"><Copy className="w-4 h-4" /> Copier</button>
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-sm font-semibold">Terminé</button>
        </div>
      </Modal>
    );
  }
  return (
    <Modal title={editing ? `Modifier — ${initial.name}` : 'Nouvel agent'} onClose={onClose} wide>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-gray-400">Nom<input value={name} onChange={e => setName(e.target.value)} className={`${inputCls} mt-1`} placeholder="Ex : Rindra" /></label>
        <label className="text-xs text-gray-400">Email de connexion<input value={email} onChange={e => setEmail(e.target.value)} disabled={editing} className={`${inputCls} mt-1 disabled:opacity-50`} placeholder="agent@exemple.com" /></label>
      </div>
      <div>
        <label className="text-xs text-gray-400">{editing ? 'Nouveau mot de passe (laisser vide pour ne pas le changer)' : 'Mot de passe (8 caractères minimum)'}</label>
        <div className="flex gap-2 mt-1">
          <input value={password} onChange={e => setPassword(e.target.value)} className={inputCls} placeholder={editing ? '••••••••' : 'Mot de passe'} />
          <button type="button" onClick={() => setPassword(randomPassword())} className="shrink-0 px-3 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Générer</button>
        </div>
      </div>
      <div>
        <div className="text-xs text-gray-400 mb-2">Onglets accessibles à cet agent ({perms.length} sélectionné{perms.length > 1 ? 's' : ''})</div>
        <div className="max-h-[46vh] overflow-y-auto pr-1"><PermPicker tabs={tabs} value={perms} onChange={setPerms} /></div>
      </div>
      {promote && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 text-sm text-amber-100 flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>Un compte existe déjà avec cet email{promote.name ? ` (${promote.name})` : ''}. Le transformer en agent ?
            <button onClick={() => submit(true)} className="ml-2 underline font-semibold">Oui, le transformer</button></div>
        </div>
      )}
      {err && <p className="text-sm text-rose-300">{err}</p>}
      <button onClick={() => submit(false)} disabled={busy} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 disabled:opacity-50 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? 'Enregistrer' : "Créer l'agent"}
      </button>
    </Modal>
  );
}

export default function StaffAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);   // { edit?: staff } | {}
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { const r = await api(''); if (r.success) setData(r); else setMsg(r.message || 'Erreur'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tabLabel = useMemo(() => Object.fromEntries((data?.tabs || []).map(t => [t.key, t.label])), [data]);
  const toggleActive = async (s) => {
    if (!s.active || window.confirm(`Désactiver le compte de ${s.name || s.email} ? Il sera déconnecté de tous ses appareils.`)) {
      setBusy(s.user_id); await api(`/${s.user_id}`, { method: 'PUT', body: JSON.stringify({ active: !s.active }) }); setBusy(''); load();
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`Retirer ${s.name || s.email} de l'équipe ?\n\nSon compte sera désactivé et il n'aura plus aucun accès à l'admin.`)) return;
    setBusy(s.user_id); const r = await api(`/${s.user_id}`, { method: 'DELETE' }); setBusy('');
    if (!r.success) setMsg(r.message || 'Erreur'); load();
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center"><ShieldCheck className="w-6 h-6" /></span> Équipe & accès</h1>
          <p className="text-sm text-blue-200 mt-1">Créez des comptes d'agent et choisissez les onglets que chacun peut voir et utiliser. Le serveur bloque tout le reste.</p>
        </div>
        <button onClick={() => setModal({})} className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-sm font-semibold flex items-center gap-2 text-white"><Plus className="w-4 h-4" /> Nouvel agent</button>
      </div>
      {msg && <p className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-2">{msg}</p>}

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-gray-400" /></div> : (
        <div className="grid gap-3">
          {(data?.staff || []).map(s => (
            <div key={s.user_id} className={`rounded-2xl border p-4 md:p-5 ${s.active ? 'bg-white/5 border-white/10' : 'bg-white/[0.02] border-white/5 opacity-70'}`}>
              <div className="flex flex-wrap items-start gap-3">
                <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center font-bold ${s.role === 'super' ? 'bg-gradient-to-br from-amber-400 to-orange-600' : 'bg-gradient-to-br from-violet-500/60 to-indigo-500/60'}`}>
                  {s.role === 'super' ? <Crown className="w-5 h-5" /> : (s.name || s.email || '?').charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="text-white">{s.name || s.email}</b>
                    {s.role === 'super' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 font-bold">Superadmin</span>}
                    {s.role === 'agent' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-500/20 border border-violet-400/40 text-violet-200 font-bold">Agent</span>}
                    {s.role === 'admin' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-gray-200 font-bold">Admin (accès complet)</span>}
                    {!s.active && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200 font-bold">Désactivé</span>}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}{s.last_login ? ` · dernière connexion ${new Date(s.last_login).toLocaleDateString('fr-FR')}` : ''}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {s.perms.includes('*')
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Tous les onglets</span>
                      : s.perms.map(k => <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.07] border border-white/12 text-gray-200">{tabLabel[k] || k}</span>)}
                  </div>
                </div>
                {s.role === 'agent' && (
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => setModal({ edit: s })} title="Modifier les accès" className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => toggleActive(s)} disabled={busy === s.user_id} title={s.active ? 'Désactiver' : 'Réactiver'} className={`p-2.5 rounded-xl ${s.active ? 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-200' : 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200'}`}><Power className="w-4 h-4" /></button>
                    <button onClick={() => remove(s)} disabled={busy === s.user_id} title="Retirer de l'équipe" className="p-2.5 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-200"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {data && data.staff.filter(s => s.role === 'agent').length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-gray-400">
              <Users className="w-10 h-10 mx-auto mb-2 text-violet-300/50" />
              <p className="font-semibold text-gray-200">Aucun agent pour l'instant</p>
              <p className="text-sm mt-1">Créez un compte et donnez-lui seulement les onglets dont il a besoin.</p>
            </div>
          )}
        </div>
      )}

      {modal && data && <StaffForm tabs={data.tabs} initial={modal.edit} onClose={() => setModal(null)} onDone={load} />}
    </div>
  );
}
